import { useContext, useMemo } from 'react';
import { AdminDashboardBarChartDataType } from './AdminDashboardBarChart.types';
import { getRandomColorForEachElement } from 'basics/utils/getRandomColors';
import { ProductsContext } from 'contexts/products';

const useAdminDashboardBarChart = (barData: AdminDashboardBarChartDataType[]) => {
  const { productList } = useContext(ProductsContext);
  const maxYValue = useMemo(() => {
    return Math.max(
      ...barData.map(({ activeCeps }) => activeCeps.reduce((sum, { countOfCeps }) => sum + countOfCeps, 0)),
    );
  }, [barData]);
  const renderCustomY = maxYValue <= 5;

  const { chartData, chartNames } = useMemo(() => {
    const categorySet = new Set<string>();

    const data = barData.map(({ period, activeCeps }) => {
      const entry: Record<string, number | string> = {
        date: new Date(period).toLocaleDateString('en-GB', {
          month: '2-digit',
          year: 'numeric',
        }),
      };

      activeCeps.forEach(({ categoryName, countOfCeps }) => {
        categorySet.add(categoryName);
        entry[categoryName] = countOfCeps;
      });

      return entry;
    });

    return {
      chartData: data,
      chartNames: Array.from(categorySet),
    };
  }, [barData]);

  const colors = useMemo(() => {
    return getRandomColorForEachElement(chartNames);
  }, [chartNames]);

  const resolveProductName = (externalProductName: string): string => productList
    .find((p) => p.productExternalID === externalProductName)?.productName || externalProductName;

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const renderLabel = (props: any) => {
    const { value, x, y, width, height } = props;

    if (
      !value
      || x == null
      || y == null
      || width == null
      || height == null
      || Math.abs(height) < 12
    ) {
      return null;
    }

    return {
      x: x + width / 2,
      y: y + height / 2,
      value,
    };
  };

  return {
    chartData,
    chartNames,
    colors,
    resolveProductName,
    renderLabel,
    renderCustomY,
  };
};

export default useAdminDashboardBarChart;
